import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { bankApi } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Landmark, RefreshCw, CheckCircle2, XCircle, AlertCircle, Link2,
  ArrowDownLeft, ArrowUpRight, Loader2, ShieldCheck, Zap, Wrench,
  Unplug, Clock, Database, ToggleLeft, ToggleRight, ExternalLink
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const PROVIDERS = [
  { id: "truelayer", name: "TrueLayer", region: "UK / EU", color: "#06B6D4", desc: "Open Banking for UK & European banks" },
  { id: "lean", name: "Lean Technologies", region: "MENA", color: "#F59E0B", desc: "Bank connectivity for UAE, Saudi, Bahrain" },
];

const STATUS_CONFIG = {
  active: { label: "Connected", color: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30", icon: CheckCircle2 },
  pending_auth: { label: "Awaiting Auth", color: "bg-amber-500/15 text-amber-400 border-amber-500/30", icon: Clock },
  connecting: { label: "Connecting...", color: "bg-cyan-500/15 text-cyan-400 border-cyan-500/30", icon: Loader2 },
  syncing: { label: "Syncing", color: "bg-cyan-500/15 text-cyan-400 border-cyan-500/30", icon: RefreshCw },
  error: { label: "Error", color: "bg-red-500/15 text-red-400 border-red-500/30", icon: AlertCircle },
  expired: { label: "Expired", color: "bg-red-500/15 text-red-400 border-red-500/30", icon: XCircle },
  disconnected: { label: "Disconnected", color: "bg-slate-500/15 text-slate-400 border-slate-500/30", icon: Unplug },
};

export default function BankPage() {
  const { formatMoney } = useAuth();
  const navigate = useNavigate();
  const [connections, setConnections] = useState([]);
  const [accounts, setAccounts] = useState({});
  const [syncRuns, setSyncRuns] = useState([]);
  const [txnData, setTxnData] = useState({ transactions: [], total: 0, matched: 0, unmatched: 0 });
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectOpen, setConnectOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [syncingId, setSyncingId] = useState(null);
  const [feedingId, setFeedingId] = useState(null);
  const [feedResult, setFeedResult] = useState(null);

  const [reconOpen, setReconOpen] = useState(false);
  const [reconTxn, setReconTxn] = useState(null);
  const [reconCategory, setReconCategory] = useState("FREE_EQUITY");
  const [reconNotes, setReconNotes] = useState("");
  const [reconLoading, setReconLoading] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [connRes, txnRes, runsRes, provRes] = await Promise.all([
        bankApi.connections().catch(() => ({ data: [] })),
        bankApi.transactions().catch(() => ({ data: { transactions: [], total: 0, matched: 0, unmatched: 0 } })),
        bankApi.syncRuns().catch(() => ({ data: [] })),
        bankApi.providers().catch(() => ({ data: [] })),
      ]);
      setConnections(connRes.data || []);
      setTxnData(txnRes.data || { transactions: [], total: 0, matched: 0, unmatched: 0 });
      setSyncRuns(runsRes.data || []);
      setProviders(provRes.data || []);

      // Load accounts for each connection
      const conns = connRes.data || [];
      const acctMap = {};
      for (const c of conns.filter(c => c.status === "active")) {
        try {
          const a = await bankApi.accounts(c.id);
          acctMap[c.id] = a.data || [];
        } catch { acctMap[c.id] = []; }
      }
      setAccounts(acctMap);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const connectBank = async () => {
    if (!selectedProvider) return;
    setConnecting(true);
    try {
      const res = await bankApi.connect(selectedProvider);
      const data = res.data;
      if (data.auth_url) {
        window.open(data.auth_url, "_blank", "width=600,height=700");
      }
      await loadAll();
      setConnectOpen(false);
      setSelectedProvider("");
    } catch (e) { console.error(e); }
    setConnecting(false);
  };

  const syncBank = async (connId) => {
    setSyncingId(connId);
    try {
      await bankApi.sync(connId);
      await loadAll();
    } catch (e) { console.error(e); }
    setSyncingId(null);
  };

  const feedToPool = async (connId) => {
    setFeedingId(connId);
    try {
      const res = await bankApi.feedToPool(connId);
      setFeedResult(res.data);
      await loadAll();
    } catch (e) { console.error(e); }
    setFeedingId(null);
  };

  const toggleAutoSync = async (connId) => {
    try {
      await bankApi.toggleAutoSync(connId);
      await loadAll();
    } catch (e) { console.error(e); }
  };

  const disconnectBank = async (connId) => {
    try {
      await bankApi.disconnect(connId);
      await loadAll();
    } catch (e) { console.error(e); }
  };

  const runAutoMatch = async () => {
    try {
      await bankApi.autoMatch();
      await loadAll();
    } catch (e) { console.error(e); }
  };

  const submitRecon = async (action) => {
    if (!reconTxn) return;
    setReconLoading(true);
    try {
      await bankApi.manualMatch({ transaction_id: reconTxn.id, action, vault_route: reconCategory, notes: reconNotes });
      await loadAll();
      setReconOpen(false);
      setReconTxn(null);
      setReconNotes("");
    } catch (e) { console.error(e); }
    setReconLoading(false);
  };

  const fmt = (iso) => {
    if (!iso) return "Never";
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  };

  const timeSince = (iso) => {
    if (!iso) return "Never";
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" /></div>;

  const activeConns = connections.filter(c => c.status !== "disconnected");
  const matched = (txnData.transactions || []).filter(t => t.matched);
  const unmatched = (txnData.transactions || []).filter(t => !t.matched);

  return (
    <div className="space-y-6" data-testid="bank-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-barlow font-bold text-2xl text-slate-50">Bank Connections</h1>
          <p className="text-sm text-slate-500 mt-0.5">Read-only mirroring. Sentient Vault never holds your funds.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={runAutoMatch} variant="outline" className="border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10 gap-2" data-testid="auto-match-btn">
            <Zap className="w-4 h-4" /> Auto-Match
          </Button>
          <Button onClick={() => setConnectOpen(true)} className="bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 gap-2" data-testid="connect-bank-btn">
            <Link2 className="w-4 h-4" /> Connect Bank
          </Button>
        </div>
      </div>

      {/* Provider Status Bar */}
      <div className="flex gap-3 flex-wrap">
        {PROVIDERS.map(p => {
          const live = providers.find(pr => pr.provider === p.id);
          const mode = live?.mode || "mock";
          return (
            <div key={p.id} className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-800/40 bg-slate-900/30" data-testid={`provider-status-${p.id}`}>
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: mode === "live" ? "#10B981" : "#64748b" }} />
              <span className="text-xs text-slate-400">{p.name}</span>
              <Badge className={`text-[8px] px-1.5 ${mode === "live" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/20" : "bg-slate-500/15 text-slate-500 border-slate-500/20"} border`}>
                {mode === "live" ? "LIVE" : "MOCK"}
              </Badge>
              <span className="text-[10px] text-slate-600">{p.region}</span>
            </div>
          );
        })}
      </div>

      {/* Connected Banks */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {activeConns.map(conn => {
          const status = STATUS_CONFIG[conn.status] || STATUS_CONFIG.error;
          const StatusIcon = status.icon;
          const connAccounts = accounts[conn.id] || [];
          const providerInfo = PROVIDERS.find(p => p.id === conn.provider);
          return (
            <Card key={conn.id} className="bg-slate-900/40 border-slate-800/50" data-testid={`bank-conn-${conn.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{
                      backgroundColor: providerInfo ? `${providerInfo.color}15` : "#64748b15",
                      border: `1px solid ${providerInfo?.color || "#64748b"}30`,
                    }}>
                      <Landmark className="w-4 h-4" style={{ color: providerInfo?.color || "#64748b" }} />
                    </div>
                    <div>
                      <span className="text-sm font-semibold text-slate-200">{conn.institution_name}</span>
                      {conn.is_simulated && <span className="text-[9px] text-slate-600 block">Mock Mode</span>}
                    </div>
                  </div>
                  <Badge className={`text-[9px] ${status.color} border gap-1`}>
                    <StatusIcon className={`w-3 h-3 ${conn.status === "syncing" ? "animate-spin" : ""}`} />
                    {status.label}
                  </Badge>
                </div>

                {/* Accounts */}
                {connAccounts.length > 0 && (
                  <div className="space-y-1.5 mb-3">
                    {connAccounts.map((acct, i) => (
                      <div key={i} className="flex justify-between items-center text-xs px-2 py-1.5 rounded bg-slate-800/30">
                        <span className="text-slate-400">{acct.account_name}</span>
                        <span className="text-slate-200 font-mono tabular">{formatMoney(acct.balance_available || 0)}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Meta */}
                <div className="space-y-1 text-xs text-slate-500">
                  <div className="flex justify-between"><span>Provider</span><span className="text-slate-300">{providerInfo?.name || conn.provider}</span></div>
                  <div className="flex justify-between"><span>Last Sync</span><span className="text-slate-300">{timeSince(conn.last_sync)}</span></div>
                  {conn.last_error && <div className="text-[10px] text-red-400 mt-1 px-2 py-1 rounded bg-red-500/5 border border-red-500/10">{conn.last_error}</div>}
                </div>

                {/* Actions */}
                <div className="mt-3 pt-3 border-t border-slate-800/30 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1 text-[10px] text-emerald-400">
                      <ShieldCheck className="w-3 h-3" /> Read-only
                    </div>
                    {conn.status === "active" && (
                      <button onClick={() => toggleAutoSync(conn.id)} className="flex items-center gap-1 text-[10px] text-slate-400 hover:text-slate-200" data-testid={`auto-sync-toggle-${conn.id}`}>
                        {conn.auto_sync ? <ToggleRight className="w-4 h-4 text-cyan-400" /> : <ToggleLeft className="w-4 h-4" />}
                        <span>Auto</span>
                      </button>
                    )}
                  </div>
                  <div className="flex gap-1.5">
                    {conn.status === "active" && (
                      <>
                        <Button size="sm" variant="outline" className="h-7 text-xs border-cyan-500/30 text-cyan-400 hover:bg-cyan-500/10"
                          onClick={() => feedToPool(conn.id)} disabled={feedingId === conn.id} data-testid={`feed-pool-btn-${conn.id}`}>
                          <Database className="w-3 h-3 mr-1" /> {feedingId === conn.id ? "Sending..." : "Feed Pool"}
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs border-slate-700"
                          onClick={() => syncBank(conn.id)} disabled={syncingId === conn.id} data-testid={`sync-btn-${conn.id}`}>
                          <RefreshCw className={`w-3 h-3 mr-1 ${syncingId === conn.id ? 'animate-spin' : ''}`} />
                          {syncingId === conn.id ? "Syncing" : "Sync"}
                        </Button>
                      </>
                    )}
                    {(conn.status === "expired" || conn.status === "error") && (
                      <Button size="sm" variant="outline" className="h-7 text-xs border-amber-500/30 text-amber-400"
                        onClick={() => { setSelectedProvider(conn.provider); setConnectOpen(true); }}>
                        Re-authorize
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-slate-600 hover:text-red-400"
                      onClick={() => disconnectBank(conn.id)} data-testid={`disconnect-btn-${conn.id}`}>
                      <Unplug className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}

        {activeConns.length === 0 && (
          <Card className="bg-slate-900/20 border-slate-800/30 border-dashed col-span-full">
            <CardContent className="p-8 text-center">
              <Landmark className="w-10 h-10 text-slate-700 mx-auto mb-3" />
              <p className="text-sm text-slate-500 mb-3">No banks connected yet</p>
              <p className="text-xs text-slate-600 max-w-md mx-auto mb-4">
                Connect your business bank to automatically mirror transactions. Read-only — Sentient Vault never moves your money.
              </p>
              <Button onClick={() => setConnectOpen(true)} className="bg-amber-600 hover:bg-amber-500 text-slate-900 gap-2" data-testid="connect-first-bank-btn">
                <Link2 className="w-4 h-4" /> Connect Your First Bank
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Feed-to-Pool Result Banner */}
      {feedResult && (
        <div className="p-3 rounded-lg border border-cyan-500/20 bg-cyan-500/5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Database className="w-4 h-4 text-cyan-400" />
            <span className="text-xs text-cyan-400">{feedResult.message}</span>
          </div>
          {feedResult.pooled > 0 && (
            <Button size="sm" variant="outline" className="h-6 text-[10px] border-cyan-500/30 text-cyan-400" onClick={() => navigate("/dashboard/data-pool")}>
              Go to Data Pool <ExternalLink className="w-3 h-3 ml-1" />
            </Button>
          )}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-slate-900/40 border-slate-800/50">
          <CardContent className="p-4">
            <div className="text-[10px] text-slate-500 uppercase tracking-wider">Total Transactions</div>
            <div className="text-xl font-mono tabular text-slate-50 mt-1">{txnData.total}</div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/40 border-emerald-500/20">
          <CardContent className="p-4">
            <div className="text-[10px] text-emerald-400 uppercase tracking-wider">Matched</div>
            <div className="text-xl font-mono tabular text-emerald-400 mt-1">{txnData.matched}</div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/40 border-amber-500/20">
          <CardContent className="p-4">
            <div className="text-[10px] text-amber-400 uppercase tracking-wider">Unmatched</div>
            <div className="text-xl font-mono tabular text-amber-400 mt-1">{txnData.unmatched}</div>
          </CardContent>
        </Card>
        <Card className="bg-slate-900/40 border-slate-800/50">
          <CardContent className="p-4">
            <div className="text-[10px] text-slate-500 uppercase tracking-wider">Match Rate</div>
            <div className="text-xl font-mono tabular text-cyan-400 mt-1">{txnData.total > 0 ? Math.round(txnData.matched / txnData.total * 100) : 0}%</div>
          </CardContent>
        </Card>
      </div>

      {/* Sync History */}
      {syncRuns.length > 0 && (
        <Card className="bg-slate-900/40 border-slate-800/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-slate-400 font-barlow">Recent Sync Runs</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-800/50 hover:bg-transparent">
                  <TableHead className="text-[10px] text-slate-500">Time</TableHead>
                  <TableHead className="text-[10px] text-slate-500">Accounts</TableHead>
                  <TableHead className="text-[10px] text-slate-500">Transactions</TableHead>
                  <TableHead className="text-[10px] text-slate-500">New</TableHead>
                  <TableHead className="text-[10px] text-slate-500">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {syncRuns.slice(0, 5).map(run => (
                  <TableRow key={run.id} className="border-slate-800/30">
                    <TableCell className="text-xs text-slate-400">{fmt(run.completed_at)}</TableCell>
                    <TableCell className="text-xs text-slate-300 font-mono">{run.accounts_synced}</TableCell>
                    <TableCell className="text-xs text-slate-300 font-mono">{run.transactions_synced}</TableCell>
                    <TableCell className="text-xs text-cyan-400 font-mono">{run.new_transactions || 0}</TableCell>
                    <TableCell>
                      <Badge className={`text-[9px] ${run.status === "completed" ? "bg-emerald-500/15 text-emerald-400" : "bg-amber-500/15 text-amber-400"} border-none`}>
                        {run.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Transactions Tabs */}
      <Tabs defaultValue="unmatched" className="space-y-4">
        <TabsList className="bg-slate-900/50 border border-slate-800/50">
          <TabsTrigger value="unmatched" className="text-xs data-[state=active]:bg-slate-800 data-[state=active]:text-amber-400">
            Unmatched ({unmatched.length})
          </TabsTrigger>
          <TabsTrigger value="matched" className="text-xs data-[state=active]:bg-slate-800 data-[state=active]:text-emerald-400">
            Matched ({matched.length})
          </TabsTrigger>
          <TabsTrigger value="all" className="text-xs data-[state=active]:bg-slate-800 data-[state=active]:text-cyan-400">
            All ({(txnData.transactions || []).length})
          </TabsTrigger>
        </TabsList>

        {["unmatched", "matched", "all"].map(tab => {
          const list = tab === "unmatched" ? unmatched : tab === "matched" ? matched : (txnData.transactions || []);
          return (
            <TabsContent key={tab} value={tab}>
              <Card className="bg-slate-900/40 border-slate-800/50">
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-800/50 hover:bg-transparent">
                        <TableHead className="text-[10px] text-slate-500">Date</TableHead>
                        <TableHead className="text-[10px] text-slate-500">Description</TableHead>
                        <TableHead className="text-[10px] text-slate-500">Counterparty</TableHead>
                        <TableHead className="text-[10px] text-slate-500 text-right">Amount</TableHead>
                        <TableHead className="text-[10px] text-slate-500">Status</TableHead>
                        <TableHead className="text-[10px] text-slate-500">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {list.length === 0 && (
                        <TableRow><TableCell colSpan={6} className="text-center text-xs text-slate-600 py-8">No transactions</TableCell></TableRow>
                      )}
                      {list.map(txn => (
                        <TableRow key={txn.id} className="border-slate-800/30" data-testid={`txn-row-${txn.id}`}>
                          <TableCell className="text-xs text-slate-400 font-mono">{fmt(txn.posted_at)}</TableCell>
                          <TableCell className="text-xs text-slate-300 max-w-[250px] truncate">{txn.description}</TableCell>
                          <TableCell className="text-xs text-slate-400">{txn.counterparty}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              {txn.amount > 0 ? <ArrowDownLeft className="w-3 h-3 text-emerald-400" /> : <ArrowUpRight className="w-3 h-3 text-red-400" />}
                              <span className={`text-sm font-mono tabular ${txn.amount > 0 ? "text-emerald-400" : "text-red-400"}`}>
                                {formatMoney(Math.abs(txn.amount))}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={`text-[9px] ${txn.matched ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" : "bg-amber-500/15 text-amber-400 border-amber-500/30"} border`}>
                              {txn.matched ? "Matched" : "Unmatched"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {!txn.matched && (
                              <Button size="sm" variant="outline" className="h-6 text-[10px] border-slate-700 text-slate-400"
                                onClick={() => { setReconTxn(txn); setReconOpen(true); }} data-testid={`recon-btn-${txn.id}`}>
                                Reconcile
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>
          );
        })}
      </Tabs>

      {/* Connect Bank Dialog */}
      <Dialog open={connectOpen} onOpenChange={setConnectOpen}>
        <DialogContent className="bg-[#0B1120] border-slate-800" data-testid="connect-bank-dialog">
          <DialogHeader>
            <DialogTitle className="font-barlow text-slate-50">Connect Bank Account</DialogTitle>
            <DialogDescription className="text-slate-500">Read-only connection. Sentient Vault never holds your funds.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {PROVIDERS.map(p => {
              const live = providers.find(pr => pr.provider === p.id);
              return (
                <button key={p.id} onClick={() => setSelectedProvider(p.id)}
                  className={`w-full p-4 rounded-xl border text-left transition-all ${selectedProvider === p.id ? 'border-amber-500/50 bg-amber-500/5' : 'border-slate-800/50 bg-slate-900/30 hover:border-slate-700/50'}`}
                  data-testid={`provider-${p.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${p.color}15`, border: `1px solid ${p.color}30` }}>
                        <Landmark className="w-4 h-4" style={{ color: p.color }} />
                      </div>
                      <div>
                        <span className="text-sm text-slate-200 font-semibold">{p.name}</span>
                        <span className="text-[10px] text-slate-500 block">{p.desc}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`text-[8px] px-1.5 ${live?.mode === "live" ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-500/15 text-slate-500"} border-none`}>
                        {live?.mode === "live" ? "LIVE" : "MOCK"}
                      </Badge>
                      {selectedProvider === p.id && <CheckCircle2 className="w-4 h-4 text-amber-400" />}
                    </div>
                  </div>
                </button>
              );
            })}
            <div className="mt-2 p-3 rounded-lg bg-slate-800/20 border border-slate-700/20">
              <p className="text-[10px] text-slate-500 leading-relaxed">
                Providers in <Badge className="text-[8px] bg-slate-500/15 text-slate-500 border-none mx-0.5">MOCK</Badge> mode will
                connect to a simulated bank with demo transactions. Add provider credentials to your environment for live banking.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setConnectOpen(false); setSelectedProvider(""); }} className="border-slate-700">Cancel</Button>
            <Button onClick={connectBank} disabled={!selectedProvider || connecting} className="bg-amber-600 hover:bg-amber-500 text-slate-900" data-testid="confirm-connect-btn">
              {connecting ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Connecting...</> : "Connect (Read-Only)"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reconciliation Dialog */}
      <Dialog open={reconOpen} onOpenChange={setReconOpen}>
        <DialogContent className="bg-[#0B1120] border-slate-800" data-testid="recon-dialog">
          <DialogHeader>
            <DialogTitle className="font-barlow text-slate-50">Reconcile Transaction</DialogTitle>
            <DialogDescription className="text-slate-500">Route to the correct vault for financial truth.</DialogDescription>
          </DialogHeader>
          {reconTxn && (
            <div className="space-y-4">
              <div className="p-3 rounded-lg bg-slate-800/40 border border-slate-700/30">
                <div className="text-xs text-slate-500">{reconTxn.description}</div>
                <div className={`text-lg font-mono tabular mt-1 ${reconTxn.amount > 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {reconTxn.amount > 0 ? "+" : ""}{formatMoney(Math.abs(reconTxn.amount))}
                </div>
                <div className="text-xs text-slate-500 mt-1">{reconTxn.counterparty} &middot; {fmt(reconTxn.posted_at)}</div>
              </div>
              <div>
                <Label className="text-xs text-slate-400">Route to Vault</Label>
                <Select value={reconCategory} onValueChange={setReconCategory}>
                  <SelectTrigger className="bg-slate-900/50 border-slate-700 mt-1" data-testid="recon-vault-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    <SelectItem value="FREE_EQUITY">Free Equity (Owned)</SelectItem>
                    <SelectItem value="SUPPLIER_OBLIGATION">Supplier Obligation</SelectItem>
                    <SelectItem value="OPERATING_CUSTODY">Operating Custody</SelectItem>
                    <SelectItem value="TAX_CUSTODY">Tax Custody</SelectItem>
                    <SelectItem value="CUSTOMER_TRUST">Customer Trust</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-400">Notes (optional)</Label>
                <Input value={reconNotes} onChange={e => setReconNotes(e.target.value)} placeholder="Add context..."
                  className="bg-slate-900/50 border-slate-700 mt-1" data-testid="recon-notes-input" />
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10" onClick={() => submitRecon("dismiss")} disabled={reconLoading} data-testid="recon-dismiss-btn">
                  Dismiss
                </Button>
                <Button onClick={() => submitRecon("categorize")} disabled={reconLoading} className="bg-cyan-600 hover:bg-cyan-500" data-testid="recon-categorize-btn">
                  {reconLoading ? "Processing..." : "Categorize & Decompose"}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
